import React, { useEffect, useState } from 'react';
import {
  Edit,
  SimpleForm,
  TextInput,
  NumberInput,
  BooleanInput,
  DateInput,
  ReferenceInput,
  SelectInput,
  ArrayInput,
  SimpleFormIterator,
  useRecordContext,
  useDataProvider,
  useUpdate,
  useNotify,
  useRedirect,
  Loading,
  TopToolbar,
  DeleteButton,
  ShowButton,
  ListButton
} from 'react-admin';
import { Box, Button } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { EntityMetadata, EntityFieldMetadata } from '../../services/types/entity-discovery.types';
import { JSONSchemaForm } from '../form/json.schema.form';
import { RJSFSchema } from '@rjsf/utils';
import { apiClient } from '../../framework/api/api.client';

interface DynamicEntityEditProps {
  entityMetadata: EntityMetadata;
  schemaKey?: string; // Optional: if provided, will fetch editor schema from backend
}

/**
 * Enhanced Dynamic Entity Editor
 * 
 * Behavior:
 * 1. If schemaKey is provided: Fetch editor schema from backend and use JSONSchemaForm
 * 2. If editorSchema is in metadata: Use JSONSchemaForm
 * 3. Fallback: Use traditional field-by-field rendering
 */
export const DynamicEntityEditV2: React.FC<DynamicEntityEditProps> = ({ 
  entityMetadata, 
  schemaKey 
}) => {
  console.log('🎨 DynamicEntityEditV2 mounted!', { 
    entityName: entityMetadata?.name,
    schemaKey,
    hasSchemaKey: !!schemaKey
  });

  const dataProvider = useDataProvider();
  const [editorSchema, setEditorSchema] = useState<RJSFSchema | null>(null);
  const [uiSchema, setUISchema] = useState<Record<string, any> | undefined>(undefined);
  const [loading, setLoading] = useState(!!schemaKey);
  const [error, setError] = useState<string | null>(null);

  // Fetch editor schema if schemaKey is provided
  useEffect(() => {
    console.log('📋 useEffect triggered', { schemaKey, hasDataProvider: !!dataProvider });
    
    if (!schemaKey) {
      console.log('⚠️ No schemaKey provided - skipping fetch, will use traditional editor');
      setLoading(false);
      return;
    }

    const fetchEditorSchema = async () => {
      try {
        setLoading(true);
        console.log(`🔍 Fetching editor schema for key: ${schemaKey}`);
        
        // Use dedicated schema-by-key endpoint (simpler and more reliable)
        // apiClient handles auth token automatically
        const schema = await apiClient.get(`/schema/key/${schemaKey}`);
        
        if (schema) {
          console.log(`✅ Found schema: ${schema.name} (key: ${schema.key})`);
          console.log(`   Has editorSchema: ${!!schema.editorSchema}`);
          
          if (schema.editorSchema) {
            const parsedSchema = typeof schema.editorSchema === 'string' 
              ? JSON.parse(schema.editorSchema)
              : schema.editorSchema;
            
            console.log(`   Parsed editorSchema with ${Object.keys(parsedSchema.properties || {}).length} fields`);
            setEditorSchema(parsedSchema);
            
            // Check if there's a uiSchema in metadata
            if (schema.configuration?.metadata?.uiSchema) {
              setUISchema(schema.configuration.metadata.uiSchema);
            }
          } else {
            console.warn(`⚠️ Schema ${schemaKey} has no editorSchema - falling back to traditional editor`);
          }
        } else {
          console.warn(`⚠️ Schema not found for key: ${schemaKey} - falling back to traditional editor`);
        }
      } catch (err) {
        console.error('❌ Failed to fetch editor schema:', err);
        setError('Failed to load editor configuration');
      } finally {
        setLoading(false);
      }
    };

    fetchEditorSchema();
  }, [schemaKey, dataProvider]);

  console.log('🎯 Render decision:', {
    loading,
    hasEditorSchema: !!editorSchema,
    editorSchemaType: editorSchema ? typeof editorSchema : 'null',
    editorSchemaFieldCount: editorSchema?.properties ? Object.keys(editorSchema.properties).length : 0,
    willUseJSONSchemaForm: !!editorSchema && !loading
  });

  if (loading) {
    console.log('⏳ Showing loading state...');
    return (
      <Edit title={`Edit ${entityMetadata.name}`}>
        <Loading />
      </Edit>
    );
  }

  const EditActions = () => (
    <TopToolbar>
      <ShowButton />
      <DeleteButton />
      <ListButton />
    </TopToolbar>
  );

  // If we have an editor schema, use JSONSchemaForm
  if (editorSchema) {
    console.log('✨ Using JSONSchemaFormEditor!', {
      fieldsCount: Object.keys(editorSchema.properties || {}).length,
      hasUISchema: !!uiSchema
    });
    return <JSONSchemaFormEditor schema={editorSchema} uiSchema={uiSchema} entityName={entityMetadata.name} actions={<EditActions />} />;
  }

  // Fallback to traditional field rendering
  console.log('⚙️ Falling back to TraditionalFieldEditor');
  return <TraditionalFieldEditor entityMetadata={entityMetadata} />;
};

/**
 * JSON Schema Form based editor
 */
const JSONSchemaFormEditor: React.FC<{ 
  schema: RJSFSchema; 
  uiSchema?: Record<string, any>; 
  entityName: string;
  actions?: JSX.Element;
}> = ({ schema, uiSchema, entityName, actions }) => {
  return (
    <Edit title={`Edit ${entityName}`} actions={actions || false}>
      <JSONSchemaFormEditorInner schema={schema} uiSchema={uiSchema} entityName={entityName} />
    </Edit>
  );
};

/**
 * Inner component that has access to the record context
 */
const JSONSchemaFormEditorInner: React.FC<{ 
  schema: RJSFSchema; 
  uiSchema?: Record<string, any>; 
  entityName: string;
}> = ({ schema, uiSchema, entityName }) => {
  const record = useRecordContext();
  const [formData, setFormData] = React.useState<Record<string, any>>(record || {});
  const [update, { isLoading }] = useUpdate();
  const notify = useNotify();
  const redirect = useRedirect();
  
  // Extract resource name from the URL (e.g., /consumer/123abc → 'consumer')
  const pathParts = window.location.pathname.split('/').filter(p => p);
  const resource = pathParts[0] || '';  // First segment is the resource name

  console.log('📝 JSONSchemaFormEditor rendering!', {
    entityName,
    resource,
    recordId: record?.id,
    schemaProperties: Object.keys(schema.properties || {}),
    recordKeys: record ? Object.keys(record) : [],
    hasUISchema: !!uiSchema,
    formDataKeys: Object.keys(formData)
  });

  // Sync record changes to form data
  React.useEffect(() => {
    if (record) {
      console.log('✅ Record loaded, updating formData:', record);
      setFormData(record);
    }
  }, [record]);

  const handleOnChange = (data: Record<string, any>) => {
    console.log('💾 JSONSchemaForm onChange:', Object.keys(data));
    setFormData(data);
  };

  const handleSave = async () => {
    console.log('💾 Saving entity:', { resource, id: record?.id, formData });
    try {
      await update(
        resource,
        { id: record?.id, data: formData },
        {
          onSuccess: () => {
            notify(`${entityName} updated successfully`, { type: 'success' });
          },
          onError: (error: any) => {
            notify(`Error: ${error.message}`, { type: 'error' });
          }
        }
      );
    } catch (err: any) {
      console.error('❌ Failed to update:', err);
      notify(`Error: ${err.message}`, { type: 'error' });
    }
  };

  return (
    <Box sx={{ maxWidth: '900px', margin: '0 auto', padding: '24px' }}>
      <JSONSchemaForm
        schema={schema}
        formData={formData}
        uiSchema={uiSchema || {}}
        onChange={handleOnChange}
      />
      <Box sx={{ marginTop: 3, display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={isLoading}
        >
          {isLoading ? 'Saving...' : 'Save'}
        </Button>
        <Button
          variant="outlined"
          onClick={() => redirect('list', resource)}
        >
          Cancel
        </Button>
      </Box>
    </Box>
  );
};

/**
 * Traditional field-by-field editor (fallback)
 */
const TraditionalFieldEditor: React.FC<{ entityMetadata: EntityMetadata }> = ({ entityMetadata }) => {
  console.log('🔧 TraditionalFieldEditor rendering (fallback mode)', {
    entityName: entityMetadata.name,
    totalFields: entityMetadata.fields?.length || 0,
    fieldNames: entityMetadata.fields?.map(f => f.name) || []
  });

  const renderInput = (field: EntityFieldMetadata) => {
    // Skip read-only fields
    if (field.name === '_id' || field.name === 'createdAt' || field.name === 'updatedAt') {
      return null;
    }
    // Skip framework fields
    if (field.name === 'context' || field.name === 'derivedBy' || field.name === '__v') {
      return null;
    }

    const commonProps = {
      key: field.name,
      source: field.name,
      label: formatFieldLabel(field.name),
      required: field.required
    };

    // Handle arrays
    if (field.isArray) {
      return (
        <ArrayInput {...commonProps}>
          <SimpleFormIterator>
            {renderSingleInput(field, { ...commonProps, source: '' })}
          </SimpleFormIterator>
        </ArrayInput>
      );
    }

    return renderSingleInput(field, commonProps);
  };

  const renderSingleInput = (field: EntityFieldMetadata, props: any) => {
    // Handle enums
    if (field.enum && field.enum.length > 0) {
      const choices = field.enum.map(value => ({ id: value, name: value }));
      return <SelectInput {...props} choices={choices} />;
    }

    // Handle references
    if (field.ref) {
      return (
        <ReferenceInput 
          {...props} 
          reference={field.ref.toLowerCase()}
        >
          <SelectInput optionText="name" />
        </ReferenceInput>
      );
    }

    // Handle different field types
    switch (field.type.toLowerCase()) {
      case 'number':
      case 'decimal128':
        return <NumberInput {...props} />;
      
      case 'boolean':
        return <BooleanInput {...props} />;
      
      case 'date':
        return <DateInput {...props} />;
      
      case 'string':
      default:
        // For nested objects (like address), provide a multiline input
        if (field.name.toLowerCase().includes('address') || 
            field.name.toLowerCase().includes('description')) {
          return <TextInput {...props} multiline rows={3} />;
        }
        // For email fields, use email input type
        if (field.name.toLowerCase().includes('email')) {
          return <TextInput {...props} type="email" />;
        }
        // For phone fields, use tel input type
        if (field.name.toLowerCase().includes('phone')) {
          return <TextInput {...props} type="tel" />;
        }
        // For URL fields, use url input type
        if (field.name.toLowerCase().includes('url')) {
          return <TextInput {...props} type="url" />;
        }
        return <TextInput {...props} />;
    }
  };

  // Get fields for edit form, excluding read-only and framework fields
  const getEditFields = () => {
    const excludedFields = ['_id', 'createdAt', 'updatedAt', '__v', 'context', 'derivedBy'];
    return entityMetadata.fields.filter(field => 
      !excludedFields.includes(field.name)
    );
  };

  const fieldsToShow = getEditFields();

  return (
    <Edit title={`Edit ${entityMetadata.name}`}>
      <SimpleForm>
        {fieldsToShow.map(field => renderInput(field))}
      </SimpleForm>
    </Edit>
  );
};

/**
 * Convert field name from camelCase to human readable format
 */
function formatFieldLabel(fieldName: string): string {
  return fieldName
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim();
}

